import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from './api';

interface User {
  _id: string;
  username: string;
  isAdmin: boolean;
  token: string;
}

interface AuthContextType {
  user: User | null;
  login: (userData: { user: Omit<User, 'token'>, token: string }) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null; // kaputter Eintrag soll nicht die ganze App lahmlegen
    }
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Beim Start die gespeicherte Sitzung pruefen: abgelaufene/ungueltige Tokens loggen sauber
  // aus (statt einen "eingeloggten" Zustand zu zeigen, in dem alles fehlschlaegt). Gueltige
  // Sitzungen bekommen ein frisches Token. Netzwerk-/Serverfehler lassen die Sitzung unangetastet.
  const hasSession = !!user;
  useEffect(() => {
    if (!hasSession) return;
    auth.me()
      .then((data) => {
        setUser({ _id: data.user._id, username: data.user.username, isAdmin: data.user.isAdmin, token: data.token });
      })
      .catch((err) => {
        if (err.response?.status === 401) setUser(null);
      });
  }, [hasSession]);

  const login = (userData: { user: Omit<User, 'token'>, token: string }) => {
    const { user: userInfo, token } = userData;
    setUser({
      _id: userInfo._id,
      username: userInfo.username,
      isAdmin: userInfo.isAdmin,
      token
    });
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth muss innerhalb eines AuthProviders verwendet werden');
  }
  return context;
}; 